import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import Cookies from 'js-cookie';

import { useAppDispatch } from '@/store';
import { setCredentials } from '@/store/slices/authSlice';
import { login, mapBackendUserToFrontend } from '@/services/api/authService';

/**
 * DevLogin - Login page; uses backend POST /auth/login.
 * Modern layout with Nestbotics and Task Management System branding.
 */
const DevLogin: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const payload = await login(email, password);
      const user = mapBackendUserToFrontend(payload.user);
      Cookies.set('authToken', payload.accessToken, { expires: 1 });
      if (payload.refreshToken) {
        Cookies.set('refreshToken', payload.refreshToken, { expires: 7 });
      }
      Cookies.set('userRole', payload.user.role, { expires: 30 });
      Cookies.set('email', payload.user.email, { expires: 30 });
      dispatch(setCredentials({ user, token: payload.accessToken }));
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#0f172a',
        overflow: 'hidden',
      }}
    >
      {/* Left panel - branding (hidden on small screens) */}
      {!isMobile && (
        <Box
          sx={{
            width: '48%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 6,
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #020617 100%)',
            borderRight: '1px solid rgba(148, 163, 184, 0.08)',
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 380 }}>
            <Typography
              component="span"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                color: 'rgba(148, 163, 184, 0.9)',
                textTransform: 'uppercase',
              }}
            >
              Nestbotics
            </Typography>
            <Typography
              variant="h3"
              sx={{
                mt: 2,
                fontWeight: 700,
                color: '#f8fafc',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Task Management System
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: 'rgba(148, 163, 184, 0.8)',
                fontSize: '1rem',
                lineHeight: 1.6,
              }}
            >
              Plan, track, and deliver work in one place. Stay in sync with your team.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Right panel - form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: isMobile ? 3 : 6,
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {isMobile && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  color: 'rgba(148, 163, 184, 0.9)',
                  textTransform: 'uppercase',
                }}
              >
                Nestbotics
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color: '#f8fafc' }}>
                Task Management System
              </Typography>
            </Box>
          )}

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#f8fafc',
              mb: 0.5,
              display: { xs: 'none', md: 'block' },
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              color: 'rgba(148, 163, 184, 0.8)',
              mb: 3,
              display: { xs: 'none', md: 'block' },
            }}
          >
            Sign in to your account
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              type="email"
              autoComplete="email"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={20} style={{ color: 'rgba(148, 163, 184, 0.7)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 1 },
                },
                '& .MuiInputLabel-root': { color: 'rgba(148, 163, 184, 0.8)' },
                '& .MuiInputBase-input': { color: '#f8fafc' },
              }}
            />
            <TextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              variant="outlined"
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={20} style={{ color: 'rgba(148, 163, 184, 0.7)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: 'rgba(148, 163, 184, 0.7)' }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 1 },
                },
                '& .MuiInputLabel-root': { color: 'rgba(148, 163, 184, 0.8)' },
                '& .MuiInputBase-input': { color: '#f8fafc' },
              }}
            />
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleLogin}
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Sign in'
              )}
            </Button>
          </Box>

          <Typography
            sx={{
              mt: 3,
              textAlign: 'center',
              fontSize: '0.75rem',
              color: 'rgba(148, 163, 184, 0.5)',
            }}
          >
            Use your Nestbotics account credentials
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DevLogin;
